import { Constants } from '../../internal/Constants';
import { FormatPhoneE164 } from '../../internal/Helpers';
import { Localized } from '../../internal/Localized';
import { useLobbySettings } from '../../providers/LobbySettingsProvider';
import { useLocale } from '../../providers/LocaleProvider';
import { useTheme } from '../../providers/ThemeProvider';
import Styles from './SharedFooter.module.css';
import { Flipbook, Image, Label } from '@app/engine';
import { WrapLTR } from '@shared/common';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

/** File-local constants for SharedFooter. */
const localConstants = {
  footerYear: '2026'
} as const;

/** Quick-link descriptor used by the footer. */
interface IQuickLink {
  label: string;
  route: string;
}

/**
 * Shared footer for the Landing and About pages.
 *
 * @returns The footer element.
 */
export function SharedFooter() {
  const { getThemedAssetDTO } = useTheme();
  const { demoE164 } = useLobbySettings();
  const { langVersion } = useLocale();

  const footerLogoAssetDTO = useMemo(() => getThemedAssetDTO(Constants.Bundle.Preload, 'LogoSmall.png'), [getThemedAssetDTO]);
  const companyFooterAssetDTO = useMemo(() => getThemedAssetDTO(Constants.Bundle.Preload, 'CompanyFooter.png'), [getThemedAssetDTO]);
  const flipbookAssetDTOs = useMemo(() => [getThemedAssetDTO(Constants.Bundle.Preload, 'Head.png'), getThemedAssetDTO(Constants.Bundle.Preload, 'HeadCall.png')], [getThemedAssetDTO]);
  const hasDemoPhone = Boolean(demoE164);
  const demoPhoneFormatted = useMemo(() => {
    void langVersion;
    if (!demoE164) return Localized.FooterDemoPhoneFallback;
    return FormatPhoneE164(demoE164);
  }, [demoE164, langVersion]);

  const quickLinks = useMemo<IQuickLink[]>(() => {
    void langVersion;
    return [
      { label: Localized.FooterQuickLinkHome, route: Constants.Route.Landing },
      { label: Localized.FooterQuickLinkDashboard, route: Constants.Route.Home },
      { label: Localized.FooterQuickLinkDemo, route: Constants.Route.AboutDemo },
      { label: Localized.FooterQuickLinkCompany, route: Constants.Route.AboutCompany },
      { label: Localized.FooterQuickLinkTerms, route: Constants.Route.AboutTerms },
      { label: Localized.FooterQuickLinkPrivacy, route: Constants.Route.AboutTerms },
      { label: Localized.FooterQuickLinkSMS, route: Constants.Route.AboutTerms },
      { label: Localized.FooterQuickLinkHelp, route: Constants.Route.AboutHelp }
    ];
  }, [langVersion]);

  return (
    <footer className={Styles.footer}>
      <div className={Styles.inner}>
        <div className={Styles.rowTop}>
          <div className={Styles.columnLeft}>
            <div className={Styles.infoBlock}>
              <Image title={Localized.FooterCompanyAlt} assetDTO={companyFooterAssetDTO} classNameContainer={Styles.companyImageWrap} classNameImageEnabled={Styles.companyImage} />
              <div className={Styles.infoTextStack}>
                <Label text={Localized.FooterCompanyName} classNameContainer={Styles.infoTitle} />
                <Label text={Localized.FooterCompanyHQ} classNameContainer={Styles.infoText} />
                <Label text={WrapLTR(Localized.FooterCompanyLegal)} classNameContainer={Styles.infoText} />
              </div>
            </div>

            <div className={Styles.infoBlock}>
              <Flipbook
                fadeMilliseconds={10}
                intervalMilliseconds={1500}
                title={Localized.AboutDemoLivePhotoAlt}
                assetDTOs={flipbookAssetDTOs}
                classNameContainer={Styles.footerFlipbookWrap}
                classNameImage={Styles.footerFlipbookImage}
                textLoading={Localized.DashLoading}
              />
              <div className={Styles.infoTextStack}>
                <Label text={Localized.AboutDemoLiveTitle} classNameContainer={Styles.infoTitle} />
                {hasDemoPhone ? (
                  <>
                    <a href={`tel:${demoE164}`} className={Styles.callLink}>
                      {WrapLTR(`${Localized.LandingDemoCallPrefix}${demoPhoneFormatted}`)}
                    </a>
                    <a href={`sms:${demoE164}`} className={Styles.textLink}>
                      {WrapLTR(`${Localized.LandingDemoTextPrefix}${demoPhoneFormatted}`)}
                    </a>
                  </>
                ) : (
                  <Label text={WrapLTR(demoPhoneFormatted)} classNameContainer={`${Styles.callLink} ${Styles.callFallback}`} />
                )}
              </div>
            </div>
          </div>

          <div className={Styles.columnRight}>
            <div className={Styles.quickLinksBlock}>
              <Label text={Localized.FooterQuickLinksTitle} classNameContainer={Styles.quickLinksTitle} />
              <div className={Styles.quickLinksList}>
                {quickLinks.map((entry) => (
                  <Link key={`${entry.label}-${entry.route}`} to={entry.route} className={Styles.quickLinkButton}>
                    {entry.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={Styles.rowMiddle}>
          <div className={`${Styles.brandBlock} ${Styles.brandBlockCentered}`}>
            <Image title={Localized.LandingLogoAlt} assetDTO={footerLogoAssetDTO} classNameContainer={Styles.logoContainer} classNameImageEnabled={Styles.footerLogo} />
            <Label text={Localized.LandingFooterTagline} classNameContainer={Styles.brandLine} />
          </div>
        </div>

        <div className={Styles.rowBottom}>
          <Label text={`${Localized.FooterCopyrightPrefix} ${localConstants.footerYear} ${Localized.FooterCompanyName}. ${Localized.FooterCopyrightSuffix}`} classNameContainer={Styles.copyright} />
          <div className={Styles.legalLinks}>
            <Link to={Constants.Route.AboutTerms} className={Styles.legalLinkButton}>
              {Localized.FooterQuickLinkPrivacy}
            </Link>
            <Label text={Constants.Separator} classNameContainer={Styles.legalSeparator} />
            <Link to={Constants.Route.AboutTerms} className={Styles.legalLinkButton}>
              {Localized.FooterQuickLinkTerms}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
